import React, { useState, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { UseCloudGameTransactions } from '@/utils/firebase.utils'
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component'
import BackButton from '@/components/SHARED/back-button/back-button.component'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRight, faXmark, faPlus } from '@fortawesome/free-solid-svg-icons'
import ImageContainer from '../image-container/image-container.component'

const TRADEABLE_TYPES = ['inventory', 'collectible', 'lottery']

const TradePortalPostDetail = ({ tradePosts, gameData, playerData, gameElements }) => {
  const { postId } = useParams()
  const navigate = useNavigate()
  const post = tradePosts?.[postId]

  const [loadingOfferId, setLoadingOfferId] = useState(null)
  const [loadingAction, setLoadingAction] = useState(null)
  const [error, setError] = useState(null)
  const [showMakeOffer, setShowMakeOffer] = useState(false)
  const [confirmAction, setConfirmAction] = useState(null)

  if (!post) {
    return (
      <div className='tp-page'>
        <div className='tp-page-header'>
          <BackButton cancel={() => navigate(-1)} />
        </div>
        <div className='tp-empty'>Trade not found.</div>
      </div>
    )
  }

  const isOwnPost = post.posterId === playerData?.playerId
  const offeringEl = gameElements?.[post.offering?.itemId]
  const myOffers = playerData
    ? Object.values(post.offers ?? {})
        .filter(o => o.offererId === playerData.playerId)
        .sort((a, b) => (b.ts_created ?? 0) - (a.ts_created ?? 0))
    : []
  const myPendingOffer = myOffers.find(o => o.status === 'pending')
  const approvedOffer = post.approvedOfferId ? post.offers?.[post.approvedOfferId] : null

  const sortedOffers = Object.values(post.offers ?? {}).sort((a, b) => {
    const order = { approved: 0, pending: 1, declined: 2 }
    return (order[a.status] ?? 1) - (order[b.status] ?? 1)
  })

  function getItemLabel(item) {
    if (item.type === 'currency') return `${item.quantity} Currency`
    if (item.type === 'points') return `${item.quantity} Points`
    const el = gameElements?.[item.itemId]
    const name = el?.name ?? item.itemId
    return item.quantity > 1 ? `${item.quantity}× ${name}` : name
  }

  async function respondToOffer(offerId, response) {
    setLoadingOfferId(offerId)
    setError(null)
    const res = await UseCloudGameTransactions('respondToTradeOffer', {
      gameId: gameData.gameId,
      playerId: playerData.playerId,
      tradeId: postId,
      offerId,
      response,
    })
    setLoadingOfferId(null)
    if (res?.error) { setError(res.error); return }
    if (response === 'approved' && !res?.awaiting) navigate(-1)
  }

  async function cancelPost() {
    setLoadingAction('cancel')
    setError(null)
    const res = await UseCloudGameTransactions('cancelTradePost', {
      gameId: gameData.gameId,
      playerId: playerData?.playerId ?? null,
      tradeId: postId,
      isManager: false,
    })
    setLoadingAction(null)
    if (res?.error) setError(res.error)
    else navigate(-1)
  }

  const canMakeOffer = post.status === 'pending' && !isOwnPost && !myPendingOffer && myOffers.length < 2

  return (
    <div className='tp-page'>
      <div className='tp-page-header'>
        <BackButton cancel={() => navigate(-1)} />
        <span className={`tp-status-pill ${post.status}`}>
          {post.status.replace('_', ' ')}
        </span>
      </div>

      {error && <div className='tp-error-banner'>{error}</div>}

      {/* Hero */}
      <div className='tp-detail-hero'>
        <div className='tp-hero-left'>
          <div className='tp-icon-wrap hero'>
            {offeringEl?.icon
              ? <ImageContainer src={offeringEl.icon} alt={offeringEl.name} />
              : <span className='tp-icon-placeholder hero'>?</span>
            }
            {post.offering?.quantity > 1 && (
              <span className='tp-qty-badge hero'>×{post.offering.quantity}</span>
            )}
          </div>
          <span className='tp-hero-item-name'>{offeringEl?.name ?? 'Item'}</span>
          <span className='tp-hero-poster'>by {post.displayName}</span>
        </div>

        {post.requesting?.length > 0 && (
          <>
            <FontAwesomeIcon icon={faArrowRight} className='tp-hero-arrow' />
            <div className='tp-hero-right'>
              <span className='tp-wants-label'>Wants</span>
              <div className='tp-wants-chips'>
                {post.requesting.map((item, i) => {
                  const el = item.itemId ? gameElements?.[item.itemId] : null
                  return (
                    <div key={i} className='tp-want-chip'>
                      {el?.icon
                        ? <ImageContainer src={el.icon} alt={el.name} className='tp-want-chip-icon' />
                        : <span className='tp-want-chip-emoji'>
                            {item.type === 'currency' ? '💰' : item.type === 'points' ? '⭐' : null}
                          </span>
                      }
                      <span>{getItemLabel(item)}</span>
                    </div>
                  )
                })}
              </div>
            </div>
          </>
        )}

        {post.requesting?.length === 0 && (
          <div className='tp-hero-open'>Open to any offer</div>
        )}
      </div>

      {/* Status banners */}
      {post.status === 'awaiting_manager' && (
        <div className='tp-status-banner awaiting'>
          ⏳ Waiting for manager to confirm the trade.
        </div>
      )}
      {post.status === 'completed' && (
        <div className='tp-status-banner completed'>🎉 Trade completed!</div>
      )}
      {(post.status === 'cancelled' || post.status === 'reversed') && (
        <div className={`tp-status-banner ${post.status}`}>
          This trade was {post.status}.
        </div>
      )}

      {/* Poster sees all offers */}
      {post.status === 'pending' && isOwnPost && (
        <div className='tp-offers-section'>
          <h3 className='tp-section-title'>
            Offers
            {sortedOffers.length > 0 && (
              <span className='tp-count-badge'>{sortedOffers.length}</span>
            )}
          </h3>
          {sortedOffers.length === 0 ? (
            <div className='tp-no-offers'>No offers yet — hang tight! 🤞</div>
          ) : (
            sortedOffers.map(offer => (
              <OfferCard
                key={offer.offerId}
                offer={offer}
                gameElements={gameElements}
                isOwnPost={true}
                loadingOfferId={loadingOfferId}
                onApprove={() => respondToOffer(offer.offerId, 'approved')}
                onDecline={() => respondToOffer(offer.offerId, 'declined')}
                getItemLabel={getItemLabel}
              />
            ))
          )}
        </div>
      )}

      {/* Browsing player: their offers */}
      {!isOwnPost && myOffers.length > 0 && (
        <div className='tp-offers-section'>
          <h3 className='tp-section-title'>Your Offer</h3>
          {myOffers.map(offer => (
            <OfferCard
              key={offer.offerId}
              offer={offer}
              gameElements={gameElements}
              isOwnPost={false}
              loadingOfferId={null}
              getItemLabel={getItemLabel}
            />
          ))}
          {post.status === 'pending' && (
            myOffers.length >= 2
              ? <div className='tp-offer-limit-note limit-reached'>Max offers reached (2 of 2) — no more offers allowed.</div>
              : myPendingOffer
                ? null
                : <div className='tp-offer-limit-note'>Offer declined — you have 1 re-offer remaining.</div>
          )}
        </div>
      )}

      {/* Make offer */}
      {canMakeOffer && !showMakeOffer && (
        <div className='tp-make-offer-cta'>
          <button className='g-button med-btn primary' onClick={() => setShowMakeOffer(true)}>
            Make an Offer
          </button>
        </div>
      )}

      {canMakeOffer && showMakeOffer && (
        <MakeOfferSection
          postId={postId}
          gameData={gameData}
          playerData={playerData}
          gameElements={gameElements}
          onCancel={() => setShowMakeOffer(false)}
          onSuccess={() => navigate(-1)}
        />
      )}

      <div className='tp-page-footer'>
        {isOwnPost && ['pending', 'awaiting_manager'].includes(post.status) && (
          confirmAction === 'cancel' ? (
            <div className='tp-confirm-row'>
              <span>Cancel this trade?</span>
              <button
                className='g-button small-btn'
                disabled={loadingAction === 'cancel'}
                onClick={cancelPost}
              >
                {loadingAction === 'cancel' ? <ThreeDotsLoader /> : 'Yes, cancel'}
              </button>
              <button className='g-button small-btn' onClick={() => setConfirmAction(null)}>
                Never mind
              </button>
            </div>
          ) : (
            <button
              className='g-button med-btn'
              onClick={() => setConfirmAction('cancel')}
            >
              Cancel Trade
            </button>
          )
        )}
        <button className='g-button med-btn' onClick={() => navigate(-1)}>Back</button>
      </div>
    </div>
  )
}

function OfferCard({ offer, gameElements, isOwnPost, loadingOfferId, onApprove, onDecline, getItemLabel }) {
  return (
    <div className={`tp-offer-card ${offer.status}`}>
      <div className='tp-offer-header'>
        <span className='tp-offerer-name'>{offer.displayName}</span>
        <span className={`tp-offer-status-pill ${offer.status}`}>{offer.status}</span>
      </div>
      <div className='tp-offer-items'>
        {offer.items?.length > 0
          ? offer.items.map((item, i) => {
              const el = item.itemId ? gameElements?.[item.itemId] : null
              return (
                <div key={i} className='tp-offer-item-chip'>
                  {el?.icon
                    ? <ImageContainer src={el.icon} alt='' className='tp-offer-item-icon' />
                    : <span className='tp-offer-emoji'>
                        {
                          item.type === 'currency' 
                          ? <ImageContainer src="/images/icons/coin.png" alt='' className='tp-offer-item-icon' /> 
                          : item.type === 'points' 
                          ? <ImageContainer src="/images/icons/xp.png" alt='' className='tp-offer-item-icon' /> 
                          : null
                        }
                      </span>
                  }
                  <span>{getItemLabel(item)}</span>
                </div>
              )
            })
          : <span className='tp-nothing-offered'>No items offered</span>
        }
      </div>
      {isOwnPost && offer.status === 'pending' && (
        <div className='tp-offer-actions'>
          <button
            className='g-button small-btn primary'
            disabled={loadingOfferId === offer.offerId}
            onClick={onApprove}
          >
            {loadingOfferId === offer.offerId ? <ThreeDotsLoader /> : '✓ Accept'}
          </button>
          <button
            className='g-button small-btn'
            disabled={loadingOfferId === offer.offerId}
            onClick={onDecline}
          >
            Decline
          </button>
        </div>
      )}
    </div>
  )
}

function MakeOfferSection({ postId, gameData, playerData, gameElements, onCancel, onSuccess }) {
  const [offerItems, setOfferItems] = useState([])
  const [addingItem, setAddingItem] = useState(false)
  const [addType, setAddType] = useState('item')
  const [addItem, setAddItem] = useState(null)
  const [addQty, setAddQty] = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const offerableItems = useMemo(() => {
    if (!playerData?.items || !gameElements) return []
    return Object.entries(playerData.items)
      .filter(([itemId, data]) => {
        const el = gameElements[itemId]
        return el && TRADEABLE_TYPES.includes(el.opt_itemType) && data.count > 0
      })
      .map(([itemId, data]) => ({ itemId, el: gameElements[itemId], count: data.count }))
      .sort((a, b) => a.el.name.localeCompare(b.el.name))
  }, [playerData?.items, gameElements])

  function getLabel(item) {
    if (item.type === 'currency') return `${item.quantity} Currency`
    const el = item.itemId ? gameElements?.[item.itemId] : null
    return item.quantity > 1 ? `${item.quantity}× ${el?.name ?? item.itemId}` : (el?.name ?? item.itemId)
  }

  function handleAddItem() {
    if (addType === 'currency') {
      if (addQty < 1) return
      setOfferItems(prev => [...prev, { type: 'currency', itemId: null, quantity: addQty }])
    } else {
      if (!addItem) return
      setOfferItems(prev => [...prev, { type: addItem.el.opt_itemType, itemId: addItem.itemId, quantity: addQty }])
    }
    setAddItem(null)
    setAddQty(1)
    setAddingItem(false)
  }

  async function handleSubmit() {
    setLoading(true)
    setError(null)
    const res = await UseCloudGameTransactions('submitTradeOffer', {
      gameId: gameData.gameId,
      playerId: playerData.playerId,
      tradeId: postId,
      items: offerItems,
    })
    setLoading(false)
    if (res?.error) { setError(res.error); return }
    onSuccess()
  }

  return (
    <div className='tp-make-offer-panel'>
      <h3 className='tp-section-title'>Your Offer</h3>
      <p className='tp-hint'>What will you give in return?</p>

      {error && <div className='tp-error-banner'>{error}</div>}

      {offerItems.length > 0 && (
        <div className='tp-request-chips'>
          {offerItems.map((item, i) => {
            const el = item.itemId ? gameElements?.[item.itemId] : null
            return (
              <div key={i} className='tp-req-chip'>
                {el?.type === 'item' 
                ? <ImageContainer src={el.icon} alt={el.name} className='tp-req-chip-icon' /> : 
                <ImageContainer src={item.type === 'currency' ? "/images/icons/coin.png" : item.type === 'points' ? "/images/icons/xp.png" : null} alt='' className='tp-req-chip-icon' />}
                <span>{getLabel(item)}</span>
                <button
                  className='tp-req-remove'
                  onClick={() => setOfferItems(prev => prev.filter((_, j) => j !== i))}
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </div>
            )
          })}
        </div>
      )}

      {!addingItem && (
        <button className='tp-add-req-btn' onClick={() => setAddingItem(true)}>
          <FontAwesomeIcon icon={faPlus} /> Add item
        </button>
      )}

      {addingItem && (
        <div className='tp-add-req-panel'>
          <div className='tp-type-toggle'>
            <button
              className={`tp-type-btn${addType === 'item' ? ' active' : ''}`}
              onClick={() => { setAddType('item'); setAddItem(null); setAddQty(1) }}
            >Item</button>
            <button
              className={`tp-type-btn${addType === 'currency' ? ' active' : ''}`}
              onClick={() => { setAddType('currency'); setAddItem(null); setAddQty(1) }}
            >Currency</button>
          </div>

          {addType === 'item' && (
            <div className='tp-icon-picker'>
              {offerableItems.map(({ itemId, el, count }) => (
                <button
                  key={itemId}
                  className={`tp-pick-item${addItem?.itemId === itemId ? ' selected' : ''}`}
                  onClick={() => setAddItem({ itemId, el })}
                >
                  <div className='tp-icon-wrap medium'>
                    {el.icon
                      ? <ImageContainer src={el.icon} alt={el.name} />
                      : <span className='tp-icon-placeholder'>?</span>
                    }
                    <span className='tp-own-qty'>{count}</span>
                  </div>
                  <span className='tp-pick-label'>{el.name}</span>
                </button>
              ))}
            </div>
          )}

          {addType === 'currency' ? (
            <div className='tp-qty-row'>
              <span className='tp-qty-label'>
                <ImageContainer src='/images/icons/coin.png' className='coin-icon' />
              </span>
              <div className='field small'>
                <input
                  type='number'
                  className='uk-input'
                  min={1}
                  max={playerData?.currency ?? 1}
                  value={addQty}
                  onChange={e => {
                    const val = Number(e.target.value)
                    setAddQty(Math.min(Math.max(1, val), playerData?.currency ?? 1))
                  }}
                />
              </div>
              <span className='tp-qty-max'>max {playerData?.currency ?? 0}</span>
            </div>
          ) : (
            <div className='tp-qty-row'>
              <span className='tp-qty-label'>Qty</span>
              <div className='tp-qty-stepper'>
                <button onClick={() => setAddQty(q => Math.max(1, q - 1))}>−</button>
                <span className='tp-qty-value'>{addQty}</span>
                <button onClick={() => setAddQty(q => q + 1)}>+</button>
              </div>
            </div>
          )}

          <div className='tp-panel-actions'>
            <button className='g-button small-btn' onClick={() => setAddingItem(false)}>Cancel</button>
            <button
              className='g-button small-btn primary'
              disabled={(addType === 'item' && !addItem) || (addType === 'currency' && (addQty < 1 || addQty > (playerData?.currency ?? 0)))}
              onClick={handleAddItem}
            >Add</button>
          </div>
        </div>
      )}

      <div className='tp-make-offer-footer'>
        <button className='g-button med-btn' onClick={onCancel}>Cancel</button>
        <button className='g-button med-btn primary' disabled={loading} onClick={handleSubmit}>
          {loading ? <ThreeDotsLoader /> : 'Send Offer'}
        </button>
      </div>
    </div>
  )
}

export default TradePortalPostDetail
